import { Box, ButtonBase, Divider, Typography, styled } from '@mui/material'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { BottomDrawer } from '$common/bottom-drawer'
import { SettingsSection } from '$common/settings-section'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { ToolBoxHeaderIcon } from '$common/icons/tool-box-header-icon'
import { breakpoints } from '$setup/breakpoints'
import { ToolBoxWappItemButton } from './tool-box-wapp-item-button'
import { ToolboxWapp } from './types'
import { t } from '$i18n'
import { CAN_SEND_WAPPS } from '$func/environmentInfo'

interface Media {
  icon: JSX.Element
  name: string
  onClick: ((userId: string) => unknown) | (() => unknown)
  disabled?: boolean | undefined
}

const Container = styled('div')({
  display: 'flex',
  padding: '8px 12px',
  justifyContent: 'space-between',
  overflow: 'scroll hidden',
  scrollbarColor: 'rgba(0, 0, 0, 0.5) transparent',
  scrollbarWidth: 'thin',
  flexWrap: 'wrap',
  '&::-webkit-scrollbar': {
    width: '1px',
    height: '2px',
  },
  '&::-webkit-scrollbar-track': {
    boxShadow: 'inset 0 0 6px #fff',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgb(204, 204, 204)',
    borderRadius: '20px',
  },
})

const StyledButtonBase = styled(ButtonBase)({
  minWidth: '80px',
  height: '100px',
})

const IconContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5px;
  width: 56px;
  height: 56px;
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  margin-bottom: 8px;
`

const WappContainer = styled('div')({
  width: '100%',
  margin: 'auto',
  position: 'relative',
  padding: '16px',
  display: 'grid',

  gridTemplateColumns: '1fr 1fr 1fr',
  gridColumnGap: '20px',
  gridRowGap: '26px',
  [breakpoints.up(20.625)]: {
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gridColumnGap: '12px',
    gridRowGap: '26px',
  },
  [breakpoints.up(23.125)]: {
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
    gridColumnGap: '0',
    gridRowGap: '26px',
  },
})

const StyledDivider = styled(Divider)({
  border: '1px solid rgba(0, 0, 0, 0.05)',
  marginRight: '16px',
  marginTop: '8px',
})

interface Props {
  onClose: () => unknown
  wapps: ToolboxWapp[]
  messageSelectorActions: Media[]
  isOpen?: boolean
}
export function ToolBoxModal({
  onClose,
  wapps,
  messageSelectorActions,
  isOpen = false,
}: Props) {
  const isDesktop = useIsDesktopSize()

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      paperProps={{ sx: isDesktop ? { maxWidth: 420 } : null }}
    >
      <Container>
        {messageSelectorActions.map(item => {
          return (
            <StyledButtonBase
              sx={{ opacity: item.disabled ? '0.6' : '' }}
              disabled={item.disabled}
              onClick={async () => {
                await closeCurrentModal()
                item.onClick('')
              }}
            >
              <Box>
                <IconContainer>{item.icon}</IconContainer>
                <Typography variant="body2">{t(item.name)}</Typography>
              </Box>
            </StyledButtonBase>
          )
        })}
      </Container>

      <StyledDivider />
      {CAN_SEND_WAPPS && (
        <SettingsSection
          headerText={t('toolBoxModal.header')}
          sx={{ marginBottom: 0 }}
          headerIcon={
            <ToolBoxHeaderIcon sx={{ width: '100%', height: '100%' }} />
          }
        >
          <WappContainer>
            {wapps.map(
              ({ icon, name, onClick, disabled, badge, loadingGradient }) => (
                <ToolBoxWappItemButton
                  icon={icon}
                  text={name}
                  onClick={() => {
                    onClose()

                    // TODO[router]: Fix by onRouterTransitionEnd
                    setTimeout(() => onClick(), 250)
                  }}
                  disabled={disabled}
                  badge={badge}
                  gradiant={loadingGradient}
                />
              ),
            )}
          </WappContainer>
        </SettingsSection>
      )}
    </BottomDrawer>
  )
}
